import { api } from "./client";
import type {
  ActivityFilters,
  CharacterJobFilters,
  CharacterMediaFilters,
} from "./studioInputs";
import {
  characterRoot,
  orgRoot,
  paidHeaders,
  queryString,
  segment,
} from "./studioRoutes";
import type {
  AnimationSource,
  ClipSource,
  GenerationRequest,
  GenerationSubmission,
} from "@/lib/studio/generation";
import type { MediaDetail, MediaListResponse } from "@/lib/studio/media";
import type {
  ActivityEntryPage,
  ActivityListResponse,
  AnchorCoverage,
  Job,
  PaidPreview,
} from "@/lib/studio/types";

const CLIP_SOURCE_KEYS = ["media_id", "proof_kind", "upload_id"] as const;

/**
 * The exact preview body for one ask, copied key by key. The server refuses
 * unknown keys, a clip key on a still, a source on References, anything but
 * one source on Animate, and on a template a prompt, source, length, a line
 * outside voice or a bed outside music; a caller's wider object (form state,
 * a spread) must never carry any of those onto the wire.
 */
function generationPreviewBody(
  input: GenerationRequest & { identity_version_id: string },
) {
  const head = {
    kind: input.kind,
    operation: input.operation,
    identity_version_id: input.identity_version_id,
  };
  if (input.kind === "clip" && input.recipe === "template") {
    return {
      ...head,
      recipe: input.recipe,
      template_id: input.template_id,
      audio: input.audio,
      ...(input.directive === "" ? {} : { directive: input.directive }),
      ...(input.audio === "voice" ? { line: input.line } : {}),
      ...(input.audio === "music" ? { bed: input.bed } : {}),
    };
  }
  const ask = { ...head, prompt: input.prompt };
  if (input.kind === "still") return { ...ask, count: input.count };
  const clip = {
    ...ask,
    recipe: input.recipe,
    length_seconds: input.length_seconds,
    ...(input.line === undefined ? {} : { line: input.line }),
  };
  if (input.recipe === "references") return clip;
  const present = CLIP_SOURCE_KEYS.filter(
    (key) => input.source[key] !== undefined,
  );
  if (present.length !== 1) {
    throw new Error("An Animate clip names exactly one local source.");
  }
  const key = present[0];
  return { ...clip, source: { [key]: input.source[key] } as ClipSource };
}

/**
 * Everything addressed under an accepted character: paid generation and its
 * sources, the library, and the two history reads. Both history cursors are
 * opaque and bound to their own endpoint; neither pages the other.
 */
export const characterApi = {
  previewGeneration: async (
    orgSlug: string,
    characterID: string,
    etag: string,
    input: GenerationRequest & { identity_version_id: string },
  ) =>
    api<{ preview: PaidPreview }>(
      `${characterRoot(orgSlug, characterID)}/generation-previews`,
      {
        method: "POST",
        headers: { "If-Match": etag },
        body: generationPreviewBody(input),
      },
    ),

  submitGeneration: (
    orgSlug: string,
    characterID: string,
    etag: string,
    idempotencyKey: string,
    input: GenerationSubmission,
  ) =>
    api<{ job: Job }>(`${characterRoot(orgSlug, characterID)}/generations`, {
      method: "POST",
      headers: paidHeaders(etag, idempotencyKey),
      body: {
        kind: input.kind,
        operation: input.operation,
        identity_version_id: input.identity_version_id,
        preview_id: input.preview_id,
      },
    }),

  /**
   * Stages one PNG or JPEG (at most 32 MiB) as an immutable Animate source
   * under the character's current ETag. The file is the only form part.
   */
  uploadAnimationSource: (
    orgSlug: string,
    characterID: string,
    etag: string,
    file: File,
  ) => {
    const formData = new FormData();
    formData.set("file", file);
    return api<AnimationSource>(
      `${characterRoot(orgSlug, characterID)}/animation-sources`,
      { method: "POST", headers: { "If-Match": etag }, formData },
    );
  },

  listCharacterMedia: (
    orgSlug: string,
    characterID: string,
    filters: CharacterMediaFilters,
  ) =>
    api<MediaListResponse>(
      `${characterRoot(orgSlug, characterID)}/media${queryString({
        q: filters.q,
        kind: filters.kind,
        cursor: filters.cursor,
        order: filters.order,
        limit: filters.limit,
        include_deleted: filters.includeDeleted === true ? "true" : undefined,
      })}`,
    ),

  getMedia: (orgSlug: string, mediaID: string) =>
    api<{ media: MediaDetail }>(
      `${orgRoot(orgSlug)}/media/${segment(mediaID)}`,
    ),

  /**
   * Recoverable removal: the row and its bytes stay, the library stops
   * showing it. There is no permanent counterpart on purpose.
   */
  deleteMedia: (orgSlug: string, mediaID: string, etag: string) =>
    api<{ media: MediaDetail }>(
      `${orgRoot(orgSlug)}/media/${segment(mediaID)}`,
      { method: "DELETE", headers: { "If-Match": etag } },
    ),

  restoreMedia: (orgSlug: string, mediaID: string, etag: string) =>
    api<{ media: MediaDetail }>(
      `${orgRoot(orgSlug)}/media/${segment(mediaID)}/restore`,
      { method: "POST", headers: { "If-Match": etag }, body: {} },
    ),

  /** Every member's jobs for this character, all time, newest first. */
  listCharacterJobs: (
    orgSlug: string,
    characterID: string,
    filters: CharacterJobFilters = {},
  ) =>
    api<ActivityListResponse>(
      `${characterRoot(orgSlug, characterID)}/jobs${queryString({
        kind: filters.kind,
        limit: filters.limit,
        cursor: filters.cursor,
      })}`,
    ),

  /** The character's curated lifecycle summaries, newest first. */
  listCharacterActivity: (
    orgSlug: string,
    characterID: string,
    filters: ActivityFilters = {},
  ) =>
    api<ActivityEntryPage>(
      `${characterRoot(orgSlug, characterID)}/activity${queryString({
        limit: filters.limit,
        cursor: filters.cursor,
      })}`,
    ),

  /** Optional live usable frames from the accepted identity's speaking orbit. */
  getAnchorCoverage: (orgSlug: string, characterID: string) =>
    api<AnchorCoverage>(
      `${characterRoot(orgSlug, characterID)}/anchor-coverage`,
    ),
};
